/**
 * Draft Store
 *
 * Centralized Pinia store for managing form drafts with persistence in localStorage.
 * Supports company/branch scoping, TTL cleanup, and automatic expiration.
 *
 * Key Features:
 * - Scoped drafts: company-level or branch-level
 * - Auto-persistence in localStorage
 * - TTL-based cleanup
 * - Unique key generation
 *
 * Key Format:
 * - Company scope: {companyId}:{action}:{entityId?}
 * - Branch scope: {companyId}:{branchId}:{action}:{entityId?}
 */

import { defineStore } from 'pinia'
import type {
  ClearDraftOptions,
  DraftEntry,
  DraftKeyConfig,
  DraftStoreState,
  GetDraftOptions,
  SaveDraftOptions,
} from '~/types/draft'

const DRAFT_STORAGE_KEY = 'openbox_drafts'
const DEFAULT_TTL_DAYS = 7

/**
 * Generate a unique draft key based on scope and identifiers
 */
function generateDraftKey(config: DraftKeyConfig): string {
  const { companyId, branchId, action, entityId, scope } = config

  const parts: string[] = [companyId]

  // Add branchId for branch-scoped drafts
  if (scope === 'branch' && branchId) {
    parts.push(branchId)
  }

  // Add action
  parts.push(action)

  // Add entityId if present (for edit operations)
  if (entityId) {
    parts.push(entityId)
  }

  return parts.join(':')
}

/**
 * Load drafts from localStorage
 */
function loadDraftsFromStorage(): Record<string, DraftEntry> {
  if (!import.meta.client) return {}

  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY)
    if (!stored) return {}
    return JSON.parse(stored) as Record<string, DraftEntry>
  } catch (error) {
    console.error('Failed to parse drafts from localStorage:', error)
    return {}
  }
}

/**
 * Save drafts to localStorage
 */
function saveDraftsToStorage(drafts: Record<string, DraftEntry>): void {
  if (!import.meta.client) return

  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(drafts))
  } catch (error) {
    console.error('Failed to serialize drafts to localStorage:', error)
    
    // Show user-facing error notification
    if (import.meta.client) {
      // Use toast notification if available
      const toast = useToast?.()
      if (toast) {
        toast.add({
          title: 'Error al guardar borrador',
          description: 'No se pudo guardar el borrador. Es posible que el almacenamiento local esté lleno o deshabilitado.',
          icon: 'i-lucide-alert-triangle',
          color: 'warning',
          timeout: 5000,
        })
      }
    }
  }
}

/**
 * Check if a draft has expired based on TTL
 */
function isDraftExpired(draft: DraftEntry, ttlDays: number): boolean {
  const now = Date.now()
  const expirationTime = draft.savedAt + ttlDays * 24 * 60 * 60 * 1000
  return now > expirationTime
}

export const useDraftStore = defineStore('draft', {
  state: (): DraftStoreState => ({
    drafts: loadDraftsFromStorage(),
  }),

  actions: {
    /**
     * Save a draft to the store and localStorage
     *
     * @param options - Options for saving the draft
     */
    saveDraft(options: SaveDraftOptions): void {
      const {
        formState,
        additionalData,
        companyId,
        branchId,
        action,
        entityId,
        scope,
        ttlDays = DEFAULT_TTL_DAYS,
      } = options

      // Generate the draft key
      const key = generateDraftKey({
        companyId,
        branchId,
        action,
        entityId,
        scope,
      })

      // Create the draft entry
      const entry: DraftEntry = {
        formState,
        additionalData,
        savedAt: Date.now(),
        scope,
        companyId,
        ...(scope === 'branch' && branchId ? { branchId } : {}),
      }

      // Save to state
      this.drafts[key] = entry

      // Persist to localStorage
      saveDraftsToStorage(this.drafts)

      // Clean expired drafts
      this.cleanExpiredDrafts(ttlDays)
    },

    /**
     * Get a draft from the store
     *
     * @param options - Options for retrieving the draft
     * @returns The draft entry if found, undefined otherwise
     */
    getDraft(options: GetDraftOptions): DraftEntry | undefined {
      const { companyId, branchId, action, entityId, scope } = options

      // Generate the draft key
      const key = generateDraftKey({
        companyId,
        branchId,
        action,
        entityId,
        scope,
      })

      return this.drafts[key]
    },

    /**
     * Check if a draft exists
     *
     * @param options - Options for checking the draft
     * @returns true if draft exists, false otherwise
     */
    hasDraft(options: GetDraftOptions): boolean {
      return this.getDraft(options) !== undefined
    },

    /**
     * Clear a specific draft from the store and localStorage
     *
     * @param options - Options for clearing the draft
     */
    clearDraft(options: ClearDraftOptions): void {
      const { companyId, branchId, action, entityId, scope } = options

      // Generate the draft key
      const key = generateDraftKey({
        companyId,
        branchId,
        action,
        entityId,
        scope,
      })

      // Remove from state
      delete this.drafts[key]

      // Persist to localStorage
      saveDraftsToStorage(this.drafts)
    },

    /**
     * Clean all expired drafts from the store
     *
     * @param ttlDays - TTL in days (default: 7)
     */
    cleanExpiredDrafts(ttlDays: number = DEFAULT_TTL_DAYS): void {
      let hasExpiredDrafts = false

      // Find and remove expired drafts
      for (const key in this.drafts) {
        const draft = this.drafts[key]
        if (isDraftExpired(draft, ttlDays)) {
          delete this.drafts[key]
          hasExpiredDrafts = true
        }
      }

      // Only persist if we actually removed drafts
      if (hasExpiredDrafts) {
        saveDraftsToStorage(this.drafts)
      }
    },

    /**
     * Clear all drafts from the store and localStorage
     * Useful for testing or manual cleanup
     */
    clearAllDrafts(): void {
      this.drafts = {}
      saveDraftsToStorage(this.drafts)
    },
  },
})
